// The signed-in top bar (direction B, Workbench): sticky over the page area. Left, the drawer button
// (below 992px) and the ⌘K menu search; right, the trial tag, Panduan, language + appearance, the
// changelog, the notification bell and the account menu. It takes over the old DashboardHeader and
// the rail footer's controls; the rail keeps the logo, the menu tree and the collapse button.
//
// 48px tall like the old header: old pages size themselves against it (`calc(100vh - 60px)`).
import { MenuIcon } from 'lucide-react';

import { useSidebar } from '@/contexts/sidebar-context';
import { useLanguage } from '@/contexts/LanguageContext';
import type { FrameState } from './AppFrame';
import { AccountMenu } from './topbar/AccountMenu';
import { CommandPalette } from './topbar/CommandPalette';
import { HelpMenu } from './topbar/HelpMenu';
import { NotificationBell } from './topbar/NotificationBell';
import { PatchLogButton } from './topbar/PatchLogButton';
import { PreferencesGroup } from './topbar/PreferencesGroup';
import { TopbarIconButton } from './topbar/TopbarIconButton';
import { TrialBadge } from './topbar/TrialBadge';

export function DashboardTopbar({ isDesktop, setMobileOpen }: FrameState) {
  const { t } = useLanguage();
  // Pages that hide the rail (PIB editor, review, export, …) have no drawer to open, and the ones with
  // tours carry their own help pill in their toolbar.
  const { isVisible: railVisible } = useSidebar();

  return (
    <header
      data-slot="app-topbar"
      className="sticky top-0 z-100 flex h-12 shrink-0 items-center gap-2 border-line border-b bg-canvas/85 px-3 backdrop-blur-md lg:px-5"
    >
      <div className="flex min-w-0 flex-1 items-center gap-1.5">
        {!isDesktop && railVisible && (
          <TopbarIconButton label={t('lumen.topbar.openMenu')} onClick={() => setMobileOpen(true)}>
            <MenuIcon />
          </TopbarIconButton>
        )}
        <CommandPalette />
      </div>
      <div className="flex shrink-0 items-center gap-1 sm:gap-1.5">
        <TrialBadge />
        {railVisible && <HelpMenu isDesktop={isDesktop} setMobileOpen={setMobileOpen} />}
        <PreferencesGroup />
        <PatchLogButton />
        <NotificationBell />
        <AccountMenu />
      </div>
    </header>
  );
}
